<template>
    <div class="grid-wrapper">
        <div class="single-university__content__holder">
            <div class="single-university__left">
                <figure v-if="university.university_logo && university.university_logo.src">
                    <img
                        :src="university.university_logo.src"
                        :alt="university.university_logo.alt_text"
                        :title="university.university_logo.title"
                    />
                </figure>
                <a class="general-button border-red" v-if="university.url" :href="university.url" target="_blank">
                    {{ printString('official_website') }}
                    <span v-html="ico_external_16" />
                </a>

                <div class="single-university__left__item" v-if="university.admissions.length > 0">
                    <h3 class="step--1 black-primary">
                        {{ printString('admissions') }}
                    </h3>
                    <ul class="single-university__left__item__list" v-if="Array.isArray(university.admissions)">
                        <li v-for="(admission, index) in university.admissions" :key="index">
                            <a class="secondary-button red-color download__button" :href="admission.url" target="_blank">
                                {{ admission.text }}
                                <span v-html="ico_external_16" />
                            </a>
                        </li>
                    </ul>
                    <div v-else v-html="university.admissions"></div>
                </div>

                <div class="single-university__left__item" v-if="university.financial.length > 0">
                    <h3 class="step--1 black-primary">
                        {{ printString('financial_aid') }}
                    </h3>
                    <ul class="single-university__left__item__list" v-if="Array.isArray(university.financial)">
                        <li v-for="(item, index) in university.financial" :key="index">
                            <a class="secondary-button red-color download__button" :href="item.url" target="_blank">
                                {{ item.text }}
                                <span v-html="ico_external_16" />
                            </a>
                        </li>
                    </ul>
                    <div v-else v-html="university.financial"></div>
                </div>

                <div class="single-university__left__item" v-if="university.international_students.length > 0">
                    <h3 class="step--1 black-primary">
                        {{ printString('international_students') }}
                    </h3>
                    <ul class="single-university__left__item__list" v-if="Array.isArray(university.international_students)">
                        <li v-for="(item, index) in university.international_students" :key="index">
                            <a class="secondary-button red-color download__button" :href="item.url" target="_blank">
                                {{ item.text }}
                                <span v-html="ico_external_16" />
                            </a>
                        </li>
                    </ul>
                    <div v-else v-html="university.international_students"></div>
                </div>

                <div class="single-university__social" v-if="university.social_media.length > 0">
                    <h3 class="step--1 black-primary">
                        {{ printString('social_media') }}
                    </h3>
                    <ul>
                        <li v-for="social in university.social_media">
                            <a :href="social.link" target="_blank">
                                <span v-html="getSocialIcon(social.social)" />
                                <span class="vh">
                                    {{ social.social }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="single-university__content">
                <figure v-if="university.university_img">
                    <img :src="university.university_img.src" :alt="university.university_img.alt_text" :title="university.university_img.title">
                </figure>

                <div class="single-university__content__wrapper">
                    
                    <p class="paragraph--teaser single-university__disclaimer margin--l-xl">
                        {{ printString('description_provided_by_institution') }}
                    </p>

                    <div v-if="university.description" class="single-university__content__about margin--xl-2xl">
                        <h3 class="step--4 black-primary margin--xs-s">
                            {{ printString('about') }}
                        </h3>
                        <div class="content paragraph--regular">
                            <div class="content paragraph--regular" v-html="displayedDescription"></div>
                            <button v-if="isDescriptionTruncated" @click="toggleDescription" class="secondary-button download__button" :class="{ 'show-less' : isShowingFullDescription, 'show-more' : !isShowingFullDescription }">
                                {{ isShowingFullDescription ? printString('show_less') : printString('show_more') }}
                                <span v-html="ico_down_16"/>
                            </button>
                        </div>

                        <button 
                                v-if="isSchoolSingle"
                                @click.prevent="goBackToPrograms" 
                                @keydown.enter.prevent="goBackToPrograms"
                                class="general-button border-red"
                        >
                            {{ printString('find_a_program') }}
                            <span v-html="ico_search_16" />
                        </button>
                    </div>

                    <div v-if="university.enrollment" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'enrollment' }]" @click="toggleSection('enrollment')">
                            <h3 class="step--4 black-primary">
                                {{ printString('enrollment') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'enrollment'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'enrollment'">
                            <div class="CollapseContent">
                                <ul>
                                    <li class="enrollment-item" v-if="university.enrollment.full_time_undergraduate">
                                        <p>
                                            {{ printString('full_time_undergraduate') }}
                                        </p>
                                        <p>{{ formatNumber(university.enrollment.full_time_undergraduate) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.enrollment.full_time_graduate">
                                        <p>
                                            {{ printString('full_time_graduate') }}
                                        </p>
                                        <p>{{ formatNumber(university.enrollment.full_time_graduate) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.enrollment.part_time_undergraduate">
                                        <p>
                                            {{ printString('part_time_undergraduate') }}
                                        </p>
                                        <p>{{ formatNumber(university.enrollment.part_time_undergraduate) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.enrollment.part_time_graduate">
                                        <p>
                                            {{ printString('part_time_graduate') }}
                                        </p>
                                        <p>{{ formatNumber(university.enrollment.part_time_graduate) }}</p>
                                    </li>
                                </ul>
                                <p class="paragraph--teaser" v-if="university.enrollment.source">
                                    {{ printString('source') }}: {{ university.enrollment.source }}
                                </p>
                            </div>
                        </Collapse>
                    </div>

                    <div v-if="university.tuition_fees" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'tuition' }]" @click="toggleSection('tuition')">
                            <h3 class="step--4 black-primary">
                                {{ printString('tuition_fees') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'tuition'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'tuition'">
                            <div class="CollapseContent">
                                <ul>
                                    <li class="enrollment-item">
                                        <p class="black-primary">
                                            {{ printString('canadian_students') }} 
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.regular_undergraduate">
                                        <p>{{ printString('canadian_undergraduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.regular_undergraduate, true) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.regular_graduate">
                                        <p>{{ printString('canadian_graduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.regular_graduate, true) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.out_of_province_undergraduate">
                                        <p>{{ printString('out_of_province_undergraduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.out_of_province_undergraduate, true) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.out_of_province_graduate">
                                        <p>{{ printString('out_of_province_graduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.out_of_province_graduate, true) }}</p>
                                    </li>
                                    <li class="enrollment-item black-primary" v-if="university.tuition_fees.international_undergraduate || university.tuition_fees.international_graduate">
                                        <p class="black-primary">
                                            {{ printString('international_students') }}
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.international_undergraduate">
                                        <p>{{ printString('international_undergraduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.international_undergraduate, true) }}</p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.tuition_fees.international_graduate">
                                        <p>{{ printString('international_graduate') }}:</p>
                                        <p> {{ formatNumber(university.tuition_fees.international_graduate, true) }}</p>
                                    </li>
                                </ul>
                                <p class="paragraph--teaser" v-if="university.tuition_fees.source">
                                    {{ printString('source') }}: {{ university.tuition_fees.source }}
                                </p>
                                <p class="paragraph--teaser" v-if="university.tuition_fees.note">
                                    {{ printString('note') }}: {{ university.tuition_fees.note }}
                                </p>
                            </div>
                        </Collapse>
                    </div>

                    <div v-if=" university.residence 
                                && university.residence.on_campus_housing !== '0' 
                                && university.residence.on_campus_housing !== null 
                                && university.residence.on_campus_housing !== ''
                                "
                                class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'residence' }]" @click="toggleSection('residence')">
                            <h3 class="step--4 black-primary">
                                {{ printString('residence') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'residence'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'residence'">
                            <div class="CollapseContent">
                                <ul>
                                    <li class="enrollment-item" v-if="university.residence.on_campus_housing && university.residence.on_campus_housing !== '0'">
                                        <p>
                                            {{ printString('on_campus_housing') }}: 
                                        </p>
                                        <p>
                                            {{  printString('available') }}
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.residence.on_campus_price_range && university.residence.on_campus_price_range !== '0' && university.residence.on_campus_price_range !== null && university.residence.on_campus_price_range !== ''">
                                        <p>
                                            {{ printString('on_campus_price_range') }}:
                                        </p>
                                        <p> 
                                            {{ formatNumber(university.residence.on_campus_price_range, true) }}
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.residence.off_campus_price_range && university.residence.off_campus_price_range !== '0' && university.residence.off_campus_price_range !== null && university.residence.off_campus_price_range !== ''">
                                        <p>
                                            {{ printString('off_campus_price_range') }}:
                                        </p>
                                        <p> 
                                            {{ formatNumber(university.residence.off_campus_price_range, true) }}
                                        </p>
                                    </li>
                                </ul>
                                <ul v-if="university.residence.coed_housing && university.residence.coed_housing !== '0' && university.residence.coed_housing !== null && university.residence.coed_housing !== ''">
                                   
                                    <li class="enrollment-item">
                                        <p class="black-primary">
                                            {{ printString('housing_options') }}
                                        </p>
                                    </li>
                                    
                                    <li class="enrollment-item">
                                        <div class="housing-options">
                                            <span v-for="option in university.residence.coed_housing" :key="option" class="housing-option" style="margin-right: var(--space-xs);">
                                                <span class="check-icon">✓</span>
                                                {{ option.charAt(0).toUpperCase() + option.slice(1) }}
                                            </span>
                                        </div>
                                    </li>
                                </ul>
                                <ul v-if="university.residence.residence_links && university.residence.residence_links !== '0' && university.residence.residence_links !== null && university.residence.residence_links !== '' && university.residence.residence_links.length > 0" >
                                   
                                   <li class="enrollment-item">
                                       <p class="black-primary">
                                           {{ printString('residence_link') }}
                                       </p>
                                   </li>
                                   
                                   <li class="enrollment-item" v-for="link in university.residence.residence_links" :key="link">
                                       <a class="secondary-button red-color button-with-icon" :href="link.url" target="_blank"> 
                                           {{ link.text }}
                                           <span v-html="ico_external_16" />
                                       </a>
                                   </li>
                               </ul>
                                <div class="paragraph--teaser" v-if="university.residence.detail && !isEmptyHtml(university.residence.detail)" v-html="university.residence.detail" />
                            </div>
                        </Collapse>
                    </div>

                    <div v-if="university.application_fee" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'applicationFee' }]" @click="toggleSection('applicationFee')">
                            <h3 class="step--4 black-primary">
                                {{ printString('application_fee') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'applicationFee'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'applicationFee'">
                            <div class="CollapseContent">
                                {{ university.application_fee }}
                            </div>
                        </Collapse>
                    </div>
                    <div v-if="university.campus_locations" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'campusLocations' }]" @click="toggleSection('campusLocations')">
                            <h3 class="step--4 black-primary">
                                {{ printString('campus_locations') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'campusLocations'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'campusLocations'">
                            <div class="CollapseContent">
                                <div v-for="(location, index) in university.campus_locations" :key="index" class="campus-location">
                                    <!-- Old API Key: AIzaSyCP11Y6zF2b2HqGUISppHsesnm0OggiakA -->
                                    <GoogleMap
                                        api-key="AIzaSyD1luwNQIEj5BRLJtUydqAkhlzujTHurGg"
                                        :center="{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }"
                                        :zoom="15"
                                        style="width: 100%; height: 300px;"
                                        mapId="YOUR_MAP_ID"
                                    >
                                        <AdvancedMarker :options="getMarkerOptions(location)" :pin-options="getPinOptions()" />
                                    </GoogleMap>
                                    <h4 class="paragraph--regular black-primary">{{ location.title }}</h4>
                                    <!-- <p>{{ location.title }}</p> -->
                                    <p>{{ location.city }}, {{ location.province }} </p>
                                    <p>{{ location.address }}</p>
                                    <p> {{ location.postal_code }}</p>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                    
                    <div v-if="university.meal_plans.length > 0 && !isEmptyHtml(university.meal_plans)" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'mealPlans' }]" @click="toggleSection('mealPlans')">
                            <h3 class="step--4 black-primary">
                                {{ printString('meal_plans') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'mealPlans'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'mealPlans'">
                            <div class="CollapseContent">
                                <div v-html="university.meal_plans" class="content"></div>
                            </div>
                        </Collapse>
                    </div>

                    <div v-if="(university.sport.varsity.length > 0 && !isEmptyHtml(university.sport.varsity)) || 
                              (university.sport.non_varsity.length > 0 && !isEmptyHtml(university.sport.non_varsity))" 
                         class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'sports' }]" @click="toggleSection('sports')">
                            <h3 class="step--4 black-primary">
                                {{ printString('sport') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'sports'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'sports'">
                            <div class="CollapseContent">
                                <ul>
                                    <li class="enrollment-item black-primary" v-if="university.sport.varsity.length > 0 && !isEmptyHtml(university.sport.varsity)">
                                        <p>
                                            {{ printString('varsity_sports') }}
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.sport.varsity.length > 0 && !isEmptyHtml(university.sport.varsity)">
                                        <div v-html="university.sport.varsity" class="content"></div>
                                    </li>
                                    <li class="enrollment-item black-primary" v-if="university.sport.non_varsity.length > 0 && !isEmptyHtml(university.sport.non_varsity)">
                                        <p>
                                            {{ printString('non_varsity_sports') }}
                                        </p>
                                    </li>
                                    <li class="enrollment-item" v-if="university.sport.non_varsity.length > 0 && !isEmptyHtml(university.sport.non_varsity)">
                                        <div v-html="university.sport.non_varsity"></div>
                                    </li>
                                </ul>
                            </div>
                        </Collapse>
                    </div>

                    <div v-if="university.support.length > 0 && !isEmptyHtml(university.support)" class="general-accordion">
                        <button :class="['Panel', { active: expandedSection === 'studentSupport' }]" @click="toggleSection('studentSupport')">
                            <h3 class="step--4 black-primary">
                                {{ printString('student_support') }}
                            </h3>
                            <span class="general-button only-icon" v-if="expandedSection !== 'studentSupport'" v-html="ico_plus_16"/>
                            <span class="general-button only-icon" v-else v-html="ico_minus_16"/>
                        </button>
                        <Collapse :when="expandedSection === 'studentSupport'">
                            <div class="CollapseContent">
                                <div v-html="university.support" class="content paragraph--regular"></div>
                            </div>
                        </Collapse>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { Collapse } from 'vue-collapsed'
import { useRoute, useRouter } from 'vue-router';
import { ref } from 'vue'
import { GoogleMap, AdvancedMarker } from 'vue3-google-map'

export default {
    
    name: "SchoolContent",
    components: {
        Collapse,
        GoogleMap,
        AdvancedMarker
    },
    props: {
        university: {
            type: Object,
            required: true,
        },
        isSchoolSingle:{
            type: Boolean,
            required: false,
            default: false,
        }
    },
    setup() {
        const expandedSection = ref(null)

        const toggleSection = (section) => {
            if (expandedSection.value === section) {
                expandedSection.value = null
            } else {
                expandedSection.value = section
            }
        }

        return {
            expandedSection,
            toggleSection
        }
    },
    data() {
        return {
            ico_external_16: window.ico_external_16,
            ico_down_16: window.ico_down_16,
            ico_minus_16: window.ico_minus_16,
            ico_plus_16: window.ico_plus_16,
            ico_search_16: window.ico_search_16,
            isShowingFullDescription: false,
            wordLimit: 80,
        }
    },
    created() {
        this.route = useRoute();
        this.router = useRouter();
    },
    computed: {
        truncatedDescription() {
            const div = document.createElement('div');
            div.innerHTML = this.university.description;

            const words = [];
            const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null, false);

            while (walker.nextNode()) {
                const node = walker.currentNode;
                const nodeWords = node.textContent.split(/\s+/);

                for (let word of nodeWords) {
                    if (word) words.push(word);
                    if (words.length >= this.wordLimit) break;
                }

                if (words.length >= this.wordLimit) {
                    // Truncate this text node
                    const remaining = this.wordLimit - (words.length - nodeWords.length);
                    node.textContent = nodeWords.slice(0, remaining).join(' ') + '...';

                    // Remove all following siblings and nodes
                    let n = node;
                    while (n && n !== div) {
                        while (n.nextSibling) n.parentNode.removeChild(n.nextSibling);
                        n = n.parentNode;
                    }
                    break;
                }
            }

            return div.innerHTML;
        },
        isDescriptionTruncated() {
            return this.stripHtml(this.university.description).split(' ').length > this.wordLimit;
        },
        displayedDescription() {
            return this.isShowingFullDescription ? this.university.description : this.truncatedDescription;
        },
    },
    methods: {
        getSocialIcon(platform) {
            return window[`ico_${platform}`];
        },
        goBackToPrograms() {
            // const route = this.site_lang === 'fr' ? '/fr/programs' : '/programs';
            const route = this.site_lang === 'fr' ? '/programs' : '/programs';
            this.router.push({ path: route });
        },
        stripHtml(html) {
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || '';
        },
        toggleDescription() {
            this.isShowingFullDescription = !this.isShowingFullDescription;
        },
        printString(string) {
            return window.translations[string];
        },
        getMarkerOptions(location) {
            return {
                position: { lat: parseFloat(location.lat), lng: parseFloat(location.lng) },
                title: location.title
            }
        },
        getPinOptions() {
            return {
                scale: 1,
                background: '#EA4435',
                borderColor: '#C6221F',
                glyphColor: '#C6221F'
            }
        },
        formatNumber(number, includeCurrency = false) {
            if (!number) return "";
            
            const formattedNumber = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, window.lang === 'fr' ? ' ' : ',');
            if (!includeCurrency) return formattedNumber;
            return window.lang === 'fr' ? `${formattedNumber} $` : `$${formattedNumber}`;
        },
        isEmptyHtml(content) {
            if (!content) return true;
            
            const removeEmptyNodes = (element) => {
                element.querySelectorAll('br').forEach((br) => br.remove());
                Array.from(element.childNodes).forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        removeEmptyNodes(node);
                        if (
                            !node.textContent.trim() &&
                            !node.querySelector('img, video, audio, iframe, embed, object, hr')
                        ) {
                            node.remove();
                        }
                    } else if (node.nodeType === Node.TEXT_NODE) {
                        if (!node.textContent.trim()) {
                            node.remove();
                        }
                    }
                });
            };

            const div = document.createElement('div');
            div.innerHTML = content;
            removeEmptyNodes(div);
            
            const hasText = div.textContent.trim().length > 0;
            const hasMedia = !!div.querySelector('img, video, audio, iframe, embed, object, hr');
            
            return !(hasText || hasMedia);
        },
    },
};
</script>






